/**
 * ComplianceTrendChart Component
 *
 * Displays compliance rate trend over time for a rule
 */

import React, { useMemo } from 'react';
import { Box, Paper, Typography, useTheme } from '@mui/material';
import type { RuleTrendDataPoint } from '@store/generated/jotaudit';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

interface ComplianceTrendChartProps {
  /** Trend data points */
  trend: RuleTrendDataPoint[];
  /** Trend granularity: 'daily', 'weekly', or 'monthly' */
  trendGranularity?: string | null;
  /** Whether data is loading */
  isLoading?: boolean;
  /** Chart height */
  height?: number;
}

const formatPercent = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  return `${value.toFixed(1)}%`;
};

const formatPeriod = (period: string, granularity?: string | null): string => {
  if (!period) return '';
  
  // For daily: "2025-01-01" -> "Jan 1"
  if (granularity === 'daily' || !granularity) {
    try {
      const date = new Date(period);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return period;
    }
  }
  
  // For weekly: "2025-W01" -> "Week 1"
  if (granularity === 'weekly') {
    return period.replace('W', 'Week ');
  }
  
  // For monthly: "2025-01" -> "Jan 2025"
  if (granularity === 'monthly') {
    try {
      const [year, month] = period.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1);
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    } catch {
      return period;
    }
  }
  
  return period;
};

export const ComplianceTrendChart: React.FC<ComplianceTrendChartProps> = ({
  trend,
  trendGranularity,
  isLoading = false,
  height = 300,
}) => {
  const theme = useTheme();

  const chartData = useMemo(() => {
    if (!trend || trend.length === 0) return [];
    
    return trend.map(point => ({
      period: point.period,
      periodLabel: formatPeriod(point.period, trendGranularity),
      complianceRate: point.compliance_rate ?? 0,
      totalAudits: point.total_audits ?? 0,
      compliantCount: point.compliant_count ?? 0,
      warningCount: point.warning_count ?? 0,
      nonCompliantCount: point.non_compliant_count ?? 0,
    }));
  }, [trend, trendGranularity]);

  if (isLoading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 2,
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Loading trend data...
        </Typography>
      </Paper>
    );
  }

  if (chartData.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 2,
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No trend data available
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 2,
      }}
    >
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Compliance Trend
      </Typography>
      <Box sx={{ width: '100%', height }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.grey[200]} />
            <XAxis
              dataKey="periodLabel"
              tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis
              domain={[0, 100]}
              tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
              tickFormatter={value => `${value}%`}
            />
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <Box
                      sx={{
                        bgcolor: 'background.paper',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        p: 1.5,
                        boxShadow: theme.shadows[3],
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        {data.periodLabel}
                      </Typography>
                      <Typography variant="body2">
                        Compliance: {formatPercent(data.complianceRate)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Total: {data.totalAudits} | Compliant: {data.compliantCount} | Warning: {data.warningCount} | Non-Compliant: {data.nonCompliantCount}
                      </Typography>
                    </Box>
                  );
                }
                return null;
              }}
            />
            <Line
              type="monotone"
              dataKey="complianceRate"
              stroke={theme.palette.primary.main}
              strokeWidth={2}
              dot={{ fill: theme.palette.primary.main, r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
};

export default ComplianceTrendChart;

